import {Button, Form, Modal, Typography} from "antd"
import {useEffect, useState} from "react"
import Label from "components/Label"
import GraphContainer from "components/graph/GraphContainer"
import PreferencesForm from "components/patient/PreferencesForm"
import VideoPlayer from "components/patient/VideoPlayer"
import {useSearchParams} from "react-router-dom"
import Impressum from "components/Impressum"

const {Paragraph, Title} = Typography

export default function Introduction() {
	const [showGraph, setShowGraph] = useState(false)
	const [activeContent, setActiveContent] = useState("how-to-use")
	const [searchParams] = useSearchParams()
	const [form] = Form.useForm()

	const activeContentClass = (content) => (content === activeContent) ? "content-active" : ""
	const currentLinkClass = (content) => (content === activeContent) ? "current-link" : ""

	const changeContent = (e, newContent) => {
		document.querySelectorAll('video').forEach(vid => vid.pause())
		setActiveContent(newContent)
		e?.currentTarget.blur()
		window.scrollTo(0, 0)
	}

	useEffect(() => {
		if (searchParams.get("edit") === "true") {
			changeContent(null, "preferences")
		}
	}, [searchParams])

	return (
		<div className="element introduction">

			<div className="side-bar">
				{
					["how-to-use", "why-this-app", "my-disease", "treatment", "preferences"].map((content, index) =>
						<Button className={`introduction-page-link ${currentLinkClass(content)} ${content}`}
								key={content}
								id={`${content}-link`}
								onClick={(e) => changeContent(e, content)}
								data-before={index + 1}
						>
							<Label id={content}/>
						</Button>)
				}

				<Button className={`introduction-page-link ${currentLinkClass("comparison")} comparison`}
						key={"comparison"}
						id={`comparison-link`}
						onClick={(e) => {
							changeContent(e, "comparison")
							setShowGraph(true)
						}}
						data-before={6}
				>
					<Label id={"comparison"}/>
				</Button>


				<Button className={`introduction-page-link ${currentLinkClass("sources")} ${"sources"}`}
						key={"sources"}
						id="sources-link"
						onClick={(e) => changeContent(e, "sources")}
						data-before={7}
				>
					<Label id="sources"/>
				</Button>
			</div>


			<div className={`introduction-page ${activeContentClass("how-to-use")}`} id="how-to-use">
				<Title level={3}><Label id="how-to-use"/></Title>
				<Paragraph>
					<Paragraph>
						Im Folgenden finden Sie verschiedene Informationen, die in 7 Abschnitte unterteilt sind:
					</Paragraph>
					<ol>
						<li>Bedienung der App</li>
						<li>Wofür diese App?</li>
						<li>Meine Erkrankung: Informationen zur Erkrankung Schizophrenie</li>
						<li>Die medikamentöse Behandlung: Einnahme von Medikamenten</li>
						<li>Was mir wichtig ist: Wünsche an die Behandlung</li>
						<li>Medikamenten-Vergleich</li>
						<li>Wissenschaftliche Quellen</li>
					</ol>
					<Paragraph>
						In welchem Abschnitt Sie sich gerade befinden, sehen Sie links. Ihr aktueller
						Abschnitt ist jeweils blau hervorgehoben. Sie können jederzeit durch Klick auf
						die Kacheln den Abschnitt wechseln. Punkt „6 Medikamenten-Vergleich“ ist
						sozusagen das Kernstück der App und bietet einen Überblick über die
						Wirksamkeit und Nebenwirkungen der verschiedenen Medikamente.
					</Paragraph>
				</Paragraph>
				<Button className="continue-button" onClick={(e) => changeContent(e, "why-this-app")}>
					<Label id="why-this-app"/>
				</Button>

				<Paragraph>
					Falls Sie direkt ihr Arztgespräch vorbereiten und alle Informationen überspringen wollen, gehen Sie direkt zu Punkt 5
				</Paragraph>
				<Button className="continue-button" onClick={(e) => changeContent(e, "preferences")}>
					<Label id="preferences"/>
				</Button>
			</div>


			<div className={`introduction-page ${activeContentClass("why-this-app")}`} id="why-this-app">
				<Title level={3}><Label id="why-this-app"/></Title>
				<Paragraph>
					<Title level={4}>
						Video 1: Einführung durch Prof. Hamann und Prof. Leucht (Entwickler)
					</Title>
					<div className="video-container">
						<VideoPlayer videoUrl="/content/Einführungsvideo.mp4" thumbnailUrl="/content/picture1.png"/>
					</div>
					<Paragraph>
						Diese App wurde entwickelt, um die gemeinsame Entscheidung zur
						medikamentösen Behandlung der Schizophrenie zwischen Ärztinnen und
						Ärzten sowie Patientinnen und Patienten zu verbessern. Hierfür haben wir
						Ihnen verschiedene Informationen zur Erkrankung zusammengestellt. Auch
						können im Abschnitt „5“ Wünsche an die Behandlung notiert werden. Unter
						„Punkt 6: Medikamentenvergleich“, dem Kernstück der App, werden Ihnen
						die wissenschaftlichen Fakten zu den verschiedenen Medikamenten gezeigt.
						Dies sind die Informationen, die auch Ihre behandelnden Ärztinnen und Ärzte
						nutzen.
					</Paragraph>
					<Paragraph>
						Außerdem haben wir aktuelle Studiendaten so aufbereitet, dass Wirksamkeit
						und Nebenwirkungen der Medikamente miteinander verglichen werden
						können. Die Daten beruhen auf einer umfangreichen Zusammenfassung
						vieler klinischer Studien (Metaanalyse) bei chronisch kranken Patienten mit
						einer akuten Schizophrenie. In diesen Studien wurden Antipsychotika
						(=Medikamente zur Linderung der Symptome) miteinander oder mit einer
						Scheinbehandlung (Placebo) über etwa sechs Wochen verglichen. Dies soll
						eine gute Grundlage für die gemeinsame Entscheidung für oder gegen die
						verschiedenen Antipsychotika bieten.
					</Paragraph>
					<Paragraph>
						Mit der App wollen wir Ihnen die Entscheidung zwischen den vielen
						verschiedenen verfügbaren Medikamenten erleichtern.
					</Paragraph>
				</Paragraph>
				<Button className="continue-button" onClick={(e) => changeContent(e, "my-disease")}>
					<Label id="my-disease"/>
				</Button>
			</div>


			<div className={`introduction-page ${activeContentClass("my-disease")}`} id="my-disease">
				<Title level={3}><Label id="my-disease"/></Title>
				<Paragraph>
					<Title level={4}>
						Video 2: Was ist Schizophrenie? (Prof. Heres)
					</Title>
					<div className="video-container">
						<VideoPlayer videoUrl="/content/Heres_Video_2.mp4" thumbnailUrl="/content/picture2.png"/>
					</div>
					<Paragraph>
						Neben Psychotherapie und anderen Behandlungsformen (z.B. Unterstützung bei der Arbeit oder im Sozialleben) nehmen
						auch Medikamente einen wichtigen Stellenwert ein. Diese sind besonders in der Akutphase dazu geeignet, die
						Symptomatik schnell zu verbessern, und helfen darüber hinaus, die Wahrscheinlichkeit für Rückfälle zu reduzieren.
						Diese Medikamente werden auch Antipsychotika genannt. Die App soll Ihnen vor allem bei der Entscheidung zwischen den
						vielen verschiedenen Antipsychotika helfen. Bei Fragen zu anderen oder zusätzlichen Therapieverfahren wenden Sie
						sich gerne an Ihren behandelnden Arzt oder Ihre behandelnde Ärztin. Auch die Internetseite
						https://www.gesundheitsinformation.de/psychotherapie-und-psychosoziale-unterstuetzung-bei-schizophrenie.html bietet
						wichtige Hinweise zum Nachlesen.
					</Paragraph>
					<Title level={4}>
						Video 3: Was aus Patient*innensicht wichtig ist bei der medikamentösen Behandlung (S. Stier)
					</Title>
					<div className="video-container">
						<VideoPlayer videoUrl="/content/Video_Patientin.mp4" thumbnailUrl="/content/picture3.png"/>
					</div>
				</Paragraph>
				<Button className="continue-button" onClick={(e) => changeContent(e, "treatment")}>
					<Label id="treatment"/>
				</Button>
			</div>


			<div className={`introduction-page ${activeContentClass("treatment")}`} id="treatment">
				<Title level={3}><Label id="treatment"/></Title>
				<Title level={4}>
					Video 4: Was es aus ärztlicher Sicht Wissenswertes über Antipsychotika gibt (Prof. Heres)
				</Title>
				<div className="video-container">
					<VideoPlayer videoUrl="/content/Heres_Video_1.mp4" thumbnailUrl="/content/picture4.png"/>
				</div>
				<Title level={4}>
					Was noch wissenswert ist:
				</Title>
				<ul>
					<li>
						Nicht jedes Medikament ist für jeden gleich gut geeignet. Hierbei spielen Alter, Erkrankungsbeginn,
						Begleiterkrankungen und andere Medikamente, die sie einnehmen, eine wichtige Rolle.
					</li>
					<li>
						Auch unterscheiden sich die zahlreichen Antipsychotika vor allem in ihren Nebenwirkungen und der Art der Einnahme.
					</li>
					<li>Die meisten Nebenwirkungen sind sowohl dosisabhängig als auch umkehrbar (reversibel)</li>
				</ul>


				<Title level={4}>
					Wie nehme ich die Medikamente ein?
				</Title>
				<Paragraph>
					Die Medikamente können als Tropfen, Tabletten oder auch als Spritze (hier dann in größeren zeitlichen Abständen, bspw.
					alle 4 Wochen) eingenommen werden.
				</Paragraph>

				<Title level={4}>
					Mit welchen Nebenwirkungen muss ich rechnen?
				</Title>
				<Paragraph>
					Bei der Einnahme von Antipsychotika kann es zu verschiedenen Nebenwirkungen kommen. Die am häufigsten genannten sind
					folgende:
				</Paragraph>
					<ul>
						<li>Mundtrockenheit, verschwommenes Sehen, Verstopfung (sog. anticholinergeNebenwirkungen)</li>
						<li>Steifigkeit der Muskulatur, Bewegungsstörungen, Zittern (sog. Parkinsonsymptome)</li>
						<li>weniger Lust auf Sex, sexuelle Funktionsstörung, Menstruationsbeschwerden</li>
						<li>Gewichtszunahme</li>
						<li>Müdigkeit</li>
						<li>Unruhige Beine</li>
					</ul>

				<Button className="continue-button" onClick={(e) => changeContent(e, "preferences")}>
					<Label id="preferences"/>
				</Button>
			</div>

			<div className={`introduction-page ${activeContentClass("preferences")}`} id="preferences">
				<PreferencesForm form={form}/>
				<Button className="continue-button showGraph" onClick={() => setShowGraph(true)}>
					<Label id="comparison"/>
				</Button>
				<Button className="continue-button savePrev" form="patientPreferences" key="submit" htmlType="submit">
					<Label id="save"/>
				</Button>
			</div>

			<div className={`introduction-page ${activeContentClass("sources")}`} id="sources">
				<ul>
					<li>
						Benkert, O., & Hippius, H. (2021). Kompendium der Psychiatrischen Pharmakotherapie (13rd Edition). Springer.
						https://doi.org/10.1007/978-3-662-61753-3
					</li>
					<li>
						DGPPN. (2019). S3 - Leitlinie Schizophrenie. AWMF-Register Nr. 038-009.
						https://www.awmf.org/uploads/tx_szleitlinien/038-009l_S3_Schizophrenie_2019-03.pdf
					</li>
					<li>
						Huhn, M., Nikolakopoulou, A., Schneider-Thoma, J., Krause, M., Samara, M., Peter, N., Arndt, T., Bäckers, L., Rothe,
						P., Cipriani, A., Davis, J., Salanti, G., & Leucht, S. (2019). Comparative efficacy and tolerability of 32 oral
						antipsychotics for the acute treatment of adults with multi-episode schizophrenia: a systematic review and network
						meta-analysis. The Lancet, 394(10202), 939-951. https://doi.org/10.1016/S0140-6736(19)31135-3
					</li>
				</ul>
				<Paragraph>
					Alle Informationen dieser Entscheidungshilfe wurden von Mitarbeiter*innen des Projekts aus Lehrbüchern und
					wissenschaftlichen Studien zusammengetragen. Die Daten können jedoch in Einzelfällen von der Fachinformation der
					jeweiligen Arzneimittel abweichen. Vor Beginn der medikamentösen Behandlung sollte daher immer die entsprechende
					Fachinformation berücksichtigt werden.
				</Paragraph>
				<Impressum/>
			</div>


			<Modal
				className="comparison"
				visible={showGraph}
				title={<Label id="comparison"/>}
				onClose={() => setShowGraph(false)}
				onCancel={() => setShowGraph(false)}
				afterClose={() => setActiveContent("preferences")}
				width={"100vw"}
				height={"100vh"}
				footer={null}
				id="comparison">

				<GraphContainer takenBefore={extractDrugPreferences(form).takenBefore}
								wouldTakeAgain={extractDrugPreferences(form).takeAgain}
								wouldNotTakeAgain={extractDrugPreferences(form).takeNotAgain}
				/>

			</Modal>

		</div>
	)
}

function extractDrugPreferences(form) {
	const takeAgain = []
	const takeNotAgain = []
	const takenBefore = []
	form?.getFieldValue("drugsTakenBefore")?.forEach(drug => {
		const wouldTake = form.getFieldValue(drug)?.wouldTakeAgain
		if (wouldTake === true) {
			takeAgain.push(drug)
		} else if (wouldTake === false) {
			takeNotAgain.push(drug)
		} else {
			takenBefore.push(drug)
		}
	})
	return {takeAgain, takeNotAgain, takenBefore}
}
